import { ChangeEvent, KeyboardEvent, useState } from 'react';
import { isFinite } from 'lodash';
import { TextField } from '@mui/material';
import { sharedColors } from '../../utils/Style';
import { useIsMobile } from '../../hooks/useIsMobile';

type NumberInputProps = {
  label?: string;
  value: number;
  onChange?: (newValue: number) => void;
  onClick?: () => void;
  acceptFloat?: boolean;
  precision?: number;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  width?: number;
};

export const NumberInput = (props: NumberInputProps) => {
  const isMobile = useIsMobile();

  const [isFocused, setIsFocused] = useState(false);
  const [plainTextValue, setPlainTextValue] = useState('');

  const parseValue = (val: string): number | undefined => {
    if (props.acceptFloat) {
      const parsed = parseFloat(val);
      return isFinite(parsed) ? parsed : undefined;
    }
    const parsed = parseInt(val, 10);
    return isFinite(parsed) ? parsed : undefined;
  };

  const getFormattedValue = (): string => {
    if (isFocused) {
      return plainTextValue;
    }

    if (isFinite(props.value)) {
      return props.acceptFloat
        ? props.value.toFixed(props.precision ?? 2)
        : props.value.toString();
    }
    return '';
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPlainTextValue(e.target.value);
  };

  const handleBlur = () => {
    setIsFocused(false);
    const parsed = parseValue(plainTextValue);
    if (props.onChange && parsed !== undefined) {
      props.onChange(parsed);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const parsed = parseValue(plainTextValue);
      if (props.onChange && parsed !== undefined) {
        props.onChange(parsed);
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    setPlainTextValue(getFormattedValue());
  };

  return (
    <TextField
      label={props.label}
      size='small'
      variant='outlined'
      type='number'
      value={getFormattedValue()}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onClick={props.onClick}
      onChange={handleChange}
      fullWidth={!props.width}
      disabled={props.disabled}
      error={props.error}
      helperText={props.helperText}
      sx={
        isMobile
          ? {
              width: props.width,
              textAlign: 'center',
              fontWeight: 500,
              fontSize: 16,
              '& input': {
                textAlign: 'center',
              },
              backgroundColor: sharedColors.white,
            }
          : { width: props.width }
      }
    />
  );
};
